import type { ReactNode } from 'react';

import type { ActorRule, RoutingTarget } from '../../shared/index.js';
import { ACTOR_RULES, ACTOR_RULE_LABELS } from '../../shared/index.js';

import type { BoardWithWorkflow, Department, DirectoryUser, JobRole } from '../../api/endpoints.js';
import { TargetPicker } from '../../components/target-picker.js';
import { Select } from '../../components/select.js';
import { Badge, Button, CONTROL_FIELD, CONTROL_SELECT } from '../../components/ui.js';
import type { MoveDraft } from './board-setup-parts.js';

type Stage = BoardWithWorkflow['workflow']['stages'][number];
type Transition = BoardWithWorkflow['workflow']['transitions'][number];

interface MutationLike<TArgs> {
  readonly isPending: boolean;
  readonly mutate: (args: TArgs) => void;
}

interface MovesSectionProps {
  readonly boardId: string;
  readonly stages: readonly Stage[];
  readonly transitions: readonly Transition[];
  readonly moveDraft: MoveDraft;
  readonly onMoveDraftChange: (next: MoveDraft) => void;
  readonly departments: readonly Department[];
  readonly jobRoles: readonly JobRole[];
  readonly people: readonly DirectoryUser[];
  readonly targetLabel: (target: RoutingTarget) => string;
  readonly addMove: MutationLike<string>;
  readonly dropMove: MutationLike<{ boardId: string; transitionId: string }>;
}

export function MovesSection({
  boardId,
  stages,
  transitions,
  moveDraft,
  onMoveDraftChange,
  departments,
  jobRoles,
  people,
  targetLabel,
  addMove,
  dropMove,
}: MovesSectionProps): ReactNode {
  const stageName = (stageId: string): string =>
    stages.find((stage) => stage.id === stageId)?.label ?? '—';

  return (
    <section className="surface-card p-5 dark:border-dark-line dark:bg-dark-panel">
      <h2 className="mb-4 text-[15px] font-bold text-navy dark:text-dark-ink">Moves</h2>

      <div className="mb-5 flex flex-col gap-2">
        {transitions.map((move) => (
          <div
            key={move.id}
            className="flex flex-wrap items-center gap-3 rounded-panel border border-hairline px-3.5 py-2.5 text-[13px] dark:border-dark-line"
          >
            <span className="font-bold text-navy dark:text-dark-ink">{move.label}</span>
            <span className="text-muted">
              {stageName(move.fromStageId)} → {stageName(move.toStageId)}
            </span>
            <Badge tone="neutral">{ACTOR_RULE_LABELS[move.actorRule]}</Badge>
            {move.actorDepartmentSlug === null ? null : (
              <Badge tone="cyan">{move.actorDepartmentSlug}</Badge>
            )}
            {move.forbidSelfService ? <Badge tone="critical">No self-approval</Badge> : null}
            {move.handOffTarget === null ? null : (
              <Badge tone="gold">Hands off to {targetLabel(move.handOffTarget)}</Badge>
            )}

            <button
              type="button"
              onClick={() => {
                dropMove.mutate({ boardId, transitionId: move.id });
              }}
              className="ml-auto text-[12.5px] font-semibold text-critical hover:underline"
            >
              Delete
            </button>
          </div>
        ))}
      </div>

      <div className="grid gap-3 border-t border-hairline pt-4 sm:grid-cols-2 lg:grid-cols-3 dark:border-dark-line">
        <label className="block">
          <span className="field-label">Label</span>
          <input
            value={moveDraft.label}
            onChange={(event) => {
              onMoveDraftChange({ ...moveDraft, label: event.target.value });
            }}
            placeholder="Approve"
            className={CONTROL_FIELD}
          />
        </label>

        <label className="block">
          <span className="field-label">From</span>
          <Select
            label="From"
            value={moveDraft.fromStageId}
            onChange={(next) => {
              onMoveDraftChange({ ...moveDraft, fromStageId: next });
            }}
            options={[
              { value: '', label: 'Choose a column' },
              ...stages.map((stage) => ({ value: stage.id, label: stage.label })),
            ]}
            className={CONTROL_SELECT}
          />
        </label>

        <label className="block">
          <span className="field-label">To</span>
          <Select
            label="To"
            value={moveDraft.toStageId}
            onChange={(next) => {
              onMoveDraftChange({ ...moveDraft, toStageId: next });
            }}
            options={[
              { value: '', label: 'Choose a column' },
              ...stages.map((stage) => ({ value: stage.id, label: stage.label })),
            ]}
            className={CONTROL_SELECT}
          />
        </label>

        <label className="block">
          <span className="field-label">Who may do it</span>
          <Select
            label="Who may do it"
            value={moveDraft.actorRule}
            onChange={(next) => {
              onMoveDraftChange({ ...moveDraft, actorRule: next as ActorRule });
            }}
            options={ACTOR_RULES.map((rule) => ({
              value: rule,
              label: ACTOR_RULE_LABELS[rule],
            }))}
            className={CONTROL_SELECT}
          />
        </label>

        {moveDraft.actorRule === 'department_member' ? (
          <label className="block">
            <span className="field-label">Which department</span>
            <Select
              label="Which department"
              value={moveDraft.actorDepartmentId}
              onChange={(next) => {
                onMoveDraftChange({ ...moveDraft, actorDepartmentId: next });
              }}
              options={[
                { value: '', label: 'Choose one' },
                ...departments.map((department) => ({
                  value: department.id,
                  label: department.name,
                })),
              ]}
              className={CONTROL_SELECT}
            />
          </label>
        ) : null}

        <div className="block">
          <span className="field-label">Hand off to</span>
          <TargetPicker
            value={moveDraft.handOffTarget}
            departments={departments}
            jobRoles={jobRoles}
            people={people}
            nowhereLabel="Nobody, it stays where it is"
            onChange={(next) => {
              onMoveDraftChange({ ...moveDraft, handOffTarget: next });
            }}
          />
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-[13px] font-semibold text-navy dark:text-dark-ink">
            <input
              type="checkbox"
              checked={moveDraft.forbidSelfService}
              onChange={(event) => {
                onMoveDraftChange({ ...moveDraft, forbidSelfService: event.target.checked });
              }}
            />
            Requester may not do it
          </label>

          <Button
            size="sm"
            className="ml-auto"
            disabled={
              moveDraft.label.trim().length < 2 ||
              moveDraft.fromStageId === '' ||
              moveDraft.toStageId === ''
            }
            loading={addMove.isPending}
            onClick={() => {
              addMove.mutate(boardId);
            }}
          >
            Add move
          </Button>
        </div>
      </div>
    </section>
  );
}
